import { Modal } from '@/components/Modal';
import { TaskModalHeader } from './TaskModalHeader';
import { TaskDescription } from './TaskDescription';
// import { SubtasksSection } from './SubtasksSection';
// import { RelatedWorkSection } from './RelatedWorkSection';
import { ActivitySection } from './ActivitySection';
import { DetailsSidebar } from './DetailsSidebar';
import { Task } from '@/types';
import { Comment } from '@/types/comments';
import { useEffect, useState, useRef, useMemo } from 'react';
import { subtaskAPI } from '@/api/subtaskAPI';
import type { Subtask } from '@/types';
import { Maximize2, Minimize2, X, Link2, MoreHorizontal, Plus, Check, Loader2 } from "lucide-react";
import toast from 'react-hot-toast';
import { wordDocApi } from '@/api/wordDocApi';
import { useNavigate } from 'react-router-dom';
import { PriorityIcon } from '@/components/PriorityIcon';
import { FileUpload } from './FileUpload';
import { mastersAPI, TaskType } from '@/api/mastersApi';
import { TaskTypeIcon } from '@/components/TaskTypeIcon';
import AvatarTooltip from '@/components/AvatarTooltip';
import { API_BASE_URL } from '@/utils/constants';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

const resolveTaskTypeIconUrl = (raw?: string | null) => {
  if (!raw) return null;
  const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
  if (builtInIcons.includes(raw.toLowerCase())) return null;
  try {
    if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    const api = new URL(API_BASE_URL);
    const origin = `${api.protocol}//${api.host}`;
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return origin + path;
  } catch {
    return null;
  }
};
import { useAppSelector } from '@/store/hooks';

// Subtask modal will be opened by parent via callback when requested

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onStatusUpdate: (taskId: string, newStatus: string) => Promise<void>;
  onAddComment: (taskId: string, html: string, parentId?: string, plainText?: string) => Promise<Comment>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onUpdateDescription: (taskId: string, description: string) => Promise<void>;
  onUpdateAssignee: (taskId: string, assigneeId: string | null) => Promise<void>;
  onUpdateReporter: (taskId: string, reporterId: string | null) => Promise<void>;
  onUpdateComment: (commentId: string, html: string, plainText?: string) => Promise<void>;
  onUpdateTaskTitle: (taskId: string, content: string) => Promise<void>; // Add this
  onUpdateFields?: (taskId: string, updates: any) => Promise<void>;
  onOpenSubtask?: (subtask: Subtask, parentTask: Task) => void;
  // Added for linter fix
  onAddSubtask?: (taskId: string, title: string) => Promise<void>;
  onUpdateSubtask?: (taskId: string, subtaskId: string, updates: any) => Promise<void>;
  subtasks?: Subtask[];
  relatedWork?: any;
  activity?: any[];
  taskDetails?: any;
  projectMembers?: any[];
  comments?: any[];
  projectKey?: string;
  workflowStatuses?: Array<{ id: string; name: string }>;
  priorities?: Array<{ id: string; name: string }>;
  project?: any;
  onRefreshSubtasks?: any;
  // When true, user is viewer-only in this project and should not be able to mutate
  isViewerOnly?: boolean;
}

export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  task,
  onStatusUpdate,
  onAddComment,
  onDeleteComment,
  onUpdateDescription,
  onUpdateAssignee,
  onUpdateReporter,
  onUpdateComment,
  activity = [],
  taskDetails = {},
  projectMembers = [],
  comments = [],
  projectKey, onUpdateTaskTitle,
  onUpdateFields,
  onOpenSubtask,
  workflowStatuses = [],
  priorities = [],
  // Added for linter fix
  onAddSubtask,
  onUpdateSubtask,
  subtasks = [],
  relatedWork = { blocks: [], relatesTo: [] },
  project,
  onRefreshSubtasks,
  isViewerOnly,
}) => {
  if (!task) return null;

  const [createSubtaskTrigger] = useState(0);
  const [subtasksInModal, setSubtasksInModal] = useState<Subtask[]>([]);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [isCreatingSubtask, setIsCreatingSubtask] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const subtaskSectionRef = useRef<HTMLDivElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [newSubtaskTypeId, setNewSubtaskTypeId] = useState<string | undefined>(undefined);
  const [isSubtaskTypePickerOpen, setIsSubtaskTypePickerOpen] = useState(false);
  const subtaskTypePickerRef = useRef<HTMLDivElement | null>(null);
  const layoutContainerRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [sidebarWidthPct, setSidebarWidthPct] = useState<number>(30);
  const [hasDragged, setHasDragged] = useState(false);
  const dragStateRef = useRef<{
    startX: number;
    startPct: number;
  } | null>(null);
  const [selectedSubtasks, setSelectedSubtasks] = useState<Set<string>>(new Set());
  const [isDeletingSubtasks, setIsDeletingSubtasks] = useState(false);
  const [showDeleteSubtasksConfirm, setShowDeleteSubtasksConfirm] = useState(false);

  const navigate = useNavigate();
  const isTaskMutating = useAppSelector((state) => state.tasks.mutating);
  const taskId = `${projectKey ? projectKey : 'AHM'}-${(task as any).display_number ?? task.id?.substring(0, 6)}`;

  const mediumPriorityId = useMemo(() => {
    if (!priorities || priorities.length === 0) return undefined;
    const match = (priorities as any[]).find((p) => {
      const key = (p.key || p.name || '').toString().toLowerCase();
      return key === 'medium';
    });
    const fallback = match || priorities[0];
    return fallback ? String(fallback.id) : undefined;
  }, [priorities]);

  const selectedSubtaskType = taskTypes.find((type) => String(type.id) === String(newSubtaskTypeId));

  const loadSubtasks = async () => {
    if (!task?.id) return;
    try {
      const items = await subtaskAPI.list(task.id);
      setSubtasksInModal(items);
    } catch { }
  };

  useEffect(() => {
    loadSubtasks();
  }, [task.id]);

  // Refresh subtasks when modal becomes visible (e.g., after closing subtask modal)
  useEffect(() => {
    if (isOpen && task?.id) {
      loadSubtasks();
    }
  }, [isOpen, task?.id]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshSubtasks) {
      // Store the refresh function reference - parent can call it
      (window as any).__refreshTaskModalSubtasks = loadSubtasks;
    }
    return () => {
      delete (window as any).__refreshTaskModalSubtasks;
    };
  }, [task?.id, onRefreshSubtasks]);

  useEffect(() => {
    (async () => {
      try {
        const types = await mastersAPI.getTaskTypes();
        setTaskTypes((types || []).filter((t) => t.is_active !== false));
      } catch {
        setTaskTypes([]);
      }
    })();
  }, []);

  const defaultSubtaskTypeId = useMemo(() => {
    if (!taskTypes.length) return undefined;
    const preferred = taskTypes.find((type) => (type.name || '').toLowerCase() === 'task');
    const fallback = preferred || taskTypes[0];
    return fallback ? String(fallback.id) : undefined;
  }, [taskTypes]);

  useEffect(() => {
    if (!taskTypes.length) return;
    setNewSubtaskTypeId((prev) => prev || defaultSubtaskTypeId);
  }, [taskTypes, defaultSubtaskTypeId]);

  useEffect(() => {
    if (!isSubtaskTypePickerOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (subtaskTypePickerRef.current && !subtaskTypePickerRef.current.contains(event.target as Node)) {
        setIsSubtaskTypePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isSubtaskTypePickerOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // Sidebar drag handlers
  const handleSidebarDragMove = (e: MouseEvent) => {
    if (!dragStateRef.current || !layoutContainerRef.current) return;
    const containerRect = layoutContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width || 1;
    const deltaX = dragStateRef.current.startX - e.clientX;
    const deltaPct = (deltaX / containerWidth) * 100;
    let nextPct = dragStateRef.current.startPct + deltaPct;

    const minPx = 260;
    const maxPct = 70;
    const minPct = (minPx / containerWidth) * 100;

    if (Number.isFinite(minPct)) {
      nextPct = Math.max(minPct, nextPct);
    }
    nextPct = Math.min(maxPct, nextPct);

    setSidebarWidthPct(nextPct);
  };

  const handleSidebarDragEnd = () => {
    dragStateRef.current = null;
    document.removeEventListener('mousemove', handleSidebarDragMove);
    document.removeEventListener('mouseup', handleSidebarDragEnd);
  };

  const startSidebarDrag: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!layoutContainerRef.current) return;
    e.preventDefault();

    let initialPct = sidebarWidthPct;

    // On first drag, derive initial width from existing layout (max-w-md etc.)
    if (!hasDragged && sidebarRef.current) {
      const containerRect = layoutContainerRef.current.getBoundingClientRect();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width || 1;
      initialPct = (sidebarRect.width / containerWidth) * 100;
      setSidebarWidthPct(initialPct);
      setHasDragged(true);
    }

    dragStateRef.current = {
      startX: e.clientX,
      startPct: initialPct,
    };
    document.addEventListener('mousemove', handleSidebarDragMove);
    document.addEventListener('mouseup', handleSidebarDragEnd);
  };

  const handleExportWord = async () => {
    try {
      const taskKey = projectKey
        ? `${projectKey}-${(task as any)?.display_number || ''}`
        : `TASK-${task.id.slice(-6)}`;
      await wordDocApi.exportTaskWord(task.id, `${taskKey}.doc`);
      setShowMoreMenu(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export task');
    }
  };

  const handleToggleSubtaskSelection = (subtaskId: string) => {
    setSelectedSubtasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subtaskId)) {
        newSet.delete(subtaskId);
      } else {
        newSet.add(subtaskId);
      }
      return newSet;
    });
  };

  const handleToggleAllSubtasks = () => {
    if (selectedSubtasks.size === subtasksInModal.length) {
      setSelectedSubtasks(new Set());
    } else {
      setSelectedSubtasks(new Set(subtasksInModal.map((s) => s.id)));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedSubtasks.size === 0) return;
    setShowDeleteSubtasksConfirm(true);
  };

  const handleConfirmDeleteSelected = async () => {
    if (selectedSubtasks.size === 0) {
      setShowDeleteSubtasksConfirm(false);
      return;
    }

    setIsDeletingSubtasks(true);
    try {
      const idsToDelete = Array.from(selectedSubtasks);
      await subtaskAPI.bulkDelete(idsToDelete);
      setSubtasksInModal((prev) => prev.filter((s) => !selectedSubtasks.has(s.id)));
      const deletedCount = selectedSubtasks.size;
      setSelectedSubtasks(new Set());
      toast.success(`Deleted ${deletedCount} subtask${deletedCount > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error deleting subtasks:', error);
      toast.error('Failed to delete subtasks');
    } finally {
      setIsDeletingSubtasks(false);
      setShowDeleteSubtasksConfirm(false);
    }
  };


  const modalContainerSizing = isMaximized
    ? 'max-w-full w-full h-full m-2 flex flex-col'
    : 'max-w-[75vw] w-full h-[82vh] flex flex-col';

  const modalContentSizing = isMaximized
    ? 'flex-1 overflow-hidden flex flex-col'
    : 'flex-1 overflow-hidden flex flex-col';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="xl"
      hideHeader
      containerClassName={`rounded-xl transition-all ${modalContainerSizing}`}
      contentClassName={`pt-4 ${modalContentSizing}`}
    >
      <div className="relative h-full">
        {/* Top toolbar (Jira-like) */}
      <div className="flex justify-between items-center pb-3 px-1 sticky top-0 bg-white z-10">
        <div className="text-sm text-gray-600 flex items-center gap-2">
          {taskId && project && (
            <button
              className="text-blue-600 hover:underline"
              onClick={() => {
                onClose();
                navigate(`/projects/${project.id}/board`);
              }}
            >
              {project.name}
            </button>
          )}
          <span className="text-gray-400">›</span>
          <span className="text-gray-800 font-medium truncate" title={taskId}>{taskId || 'Task'}</span>
        </div>

        <div className='flex items-center gap-2 ml-auto'>
          <div className="relative group inline-block">
            <button
              type="button"
              onClick={async () => {
                const url = `${window.location.origin}/task/${task.id}`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied to clipboard");
                } catch (err) {
                  const textArea = document.createElement("textarea");
                  textArea.value = url;
                  textArea.style.position = "fixed";
                  textArea.style.opacity = "0";
                  document.body.appendChild(textArea);
                  textArea.select();
                  try {
                    document.execCommand("copy");
                    toast.success("Link copied to clipboard");
                  } catch (fallbackErr) {
                    toast.error("Failed to copy link");
                  }
                  document.body.removeChild(textArea);
                }
              }}
              className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors px-2 py-1 rounded hover:bg-gray-100"
            >
              <Link2 className="w-4 h-4" />
            </button>

            {/* Tooltip */}
            <div className="absolute left-1/2 -translate-x-1/2 top-8 
                  opacity-0 group-hover:opacity-100 
                  transition bg-gray-900 text-white text-xs
                  py-1 px-2 rounded shadow-lg whitespace-nowrap 
                  pointer-events-none">
              Copy shareable link
              <div className="absolute left-1/2 -translate-x-1/2 top-full 
                    border-4 border-transparent border-b-gray-900"></div>
            </div>
          </div>
          <div className="relative" ref={moreMenuRef}>
            <button
              type="button"
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
              aria-label="More options"
              onClick={() => setShowMoreMenu(!showMoreMenu)}
            >
              <MoreHorizontal size={18} />
            </button>
            {showMoreMenu && (
              <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  onClick={handleExportWord}
                >
                  Export Word
                </button>
              </div>
            )}
          </div>
          <button
            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
            aria-label={isMaximized ? "Restore" : "Expand"}
            onClick={() => setIsMaximized(!isMaximized)}
          >
            {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <button className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded" aria-label="Close" onClick={onClose}>✕</button>
        </div>
      </div>

      <div
        ref={layoutContainerRef}
        className={`flex flex-1 ${isMaximized ? 'min-h-0' : 'h-[65vh]'}`}
      >
        {/* Main Content - Left Side */}
        <div
          className="flex-1 pr-6 overflow-y-auto taskmodal-scroll"
          style={hasDragged ? {
            flexBasis: `${100 - sidebarWidthPct}%`,
            minWidth: '260px',
          } : undefined}
        >
          <TaskModalHeader
            isViewerOnly={isViewerOnly}
            task={task}
            onStatusUpdate={(newStatus) => onStatusUpdate(task.id, newStatus)}
            commentsLength={activity.length}
            projectKey={projectKey}
            onTitleUpdate={(newTitle) => onUpdateTaskTitle(task.id, newTitle)}
            workflowStatuses={workflowStatuses}
            projectId={project?.id}
          />

          {/* Jira-like inline action under title */}
          <div className="-mt-2 mb-2">
            {!isViewerOnly && (
            <button
              type="button"
              className="flex items-center justify-between px-3 py-1 gap-1 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
              onClick={() => {
                setShowAddSubtask(true);
                if (!newSubtaskTypeId && defaultSubtaskTypeId) {
                  setNewSubtaskTypeId(defaultSubtaskTypeId);
                }
                setTimeout(() => {
                  subtaskSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  addInputRef.current?.focus();
                }, 0);
              }}
            >
              <Plus className="w-3 h-3" />
              <span>Create subtask</span>
            </button>
            )}
          </div>

          <TaskDescription
            disabled={isViewerOnly}
            task={task}
            onDescriptionUpdate={(description) => onUpdateDescription(task.id, description)}
          />

          {/* Subtasks under description (Jira-like) */}
          <div className="mt-4" ref={subtaskSectionRef}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Subtasks {subtasksInModal.length ? `(${subtasksInModal.length})` : ''}</h4>
              {!showAddSubtask && !isViewerOnly && (
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setShowAddSubtask(true);
                    if (!newSubtaskTypeId && defaultSubtaskTypeId) {
                      setNewSubtaskTypeId(defaultSubtaskTypeId);
                    }
                    setTimeout(() => {
                      subtaskSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      addInputRef.current?.focus();
                    }, 0);
                  }}
                >
                  Add subtask
                </button>
              )}
            </div>

              {/* Jira-like list with columns (hidden if empty) */}
              {subtasksInModal.length > 0 && (
                <div className="border border-gray-200 rounded">
                  <div className="grid grid-cols-12 items-center text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-t">
                    <div className="col-span-1 flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={selectedSubtasks.size === subtasksInModal.length && subtasksInModal.length > 0}
                        onChange={() => {
                        if(isViewerOnly) return true;
                        handleToggleAllSubtasks()
                        }}
                        className="rounded border-gray-300 cursor-pointer"
                        title="Select all"
                      />
                    </div>
                    <div className="col-span-4">Work</div>
                    <div className="col-span-2 text-center">Code</div>
                    <div className="col-span-1 text-center">Pri</div>
                    <div className="col-span-2 text-center">Ass</div>
                    <div className="col-span-2 text-center">Status</div>
                  </div>
                  {subtasksInModal.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="w-full grid grid-cols-12 items-center text-left px-3 py-2 hover:bg-gray-50 border-t border-gray-100"
                      onClick={(e) => {
                        // Don't open modal when clicking checkbox
                        if ((e.target as HTMLElement).tagName !== 'INPUT') {
                          onOpenSubtask?.(s, task);
                        }
                      }}
                    >
                      <div className="col-span-1 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedSubtasks.has(s.id)}
                          onChange={() => {
                            if(isViewerOnly) return true;
                            handleToggleSubtaskSelection(s.id)
                           }}
                          className="rounded border-gray-300 cursor-pointer"
                        />
                      </div>
                      <div className="col-span-4 flex items-center gap-2">
                        {(() => {
                          // Prefer explicit task_type_id from API; fall back to name-based lookup from task_type
                          const rawTypeId = (s as any).task_type_id;
                          let subtaskType =
                            rawTypeId && taskTypes.length
                              ? taskTypes.find((t) => String(t.id) === String(rawTypeId))
                              : undefined;

                          if (!subtaskType && taskTypes.length) {
                            const rawTypeName = ((s as any).task_type || '').toString().toLowerCase();
                            if (rawTypeName) {
                              subtaskType = taskTypes.find(
                                (t) => (t.name || '').toString().toLowerCase() === rawTypeName
                              );
                            }
                          }

                          const iconUrl = subtaskType
                            ? resolveTaskTypeIconUrl(
                              (subtaskType as any)?.icon ||
                              (subtaskType as any)?.icon_url ||
                              (subtaskType as any)?.image ||
                              (subtaskType as any)?.image_url
                            )
                            : undefined;
                          const label = subtaskType?.name || 'Task';

                          return (
                            <AvatarTooltip label={label} position="bottom">
                              <div className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center bg-white">
                                <TaskTypeIcon
                                  url={iconUrl}
                                  name={label}
                                  className="w-4 h-4"
                                />
                              </div>
                            </AvatarTooltip>
                          );
                        })()}
                        <span className="truncate">{s.title}</span>
                      </div>
                      {/* Code */}
                      <div className="col-span-2 text-center">
                        {(() => {
                          const parentCode = `${projectKey || ''}-${(task as any)?.display_number ?? ''}`.replace(/-$/, '');
                          const subDisplay = (s as any).display_number || (s as any).code;
                          const fallback = String(s.id).slice(-4).toUpperCase();
                          return subDisplay ? `${parentCode ? parentCode + '.' : ''}${subDisplay}` : (parentCode ? `${parentCode}.${fallback}` : fallback);
                        })()}
                      </div>
                      {/* Priority */}
                      <div className="col-span-1 text-center">
                        {(() => {
                          const pid = (s as any).priority_id;
                          if (pid && Array.isArray(priorities) && priorities.length > 0) {
                            const priority = priorities.find((pp: any) => String(pp.id) === String(pid));
                            if (priority) {
                              return (
                                <AvatarTooltip
                                  label={`${priority.name} Priority`}
                                  position="bottom"
                                >
                                  <PriorityIcon
                                    priority={priority}
                                    size={16}
                                    className="cursor-pointer"
                                  />
                                </AvatarTooltip>
                              );
                            }
                          }
                          return '-';
                        })()}
                      </div>
                      {/* Assignee */}
                      <div className="col-span-2 text-center">
                        {(() => {
                          const assigneeId = (s as any).assigned_to;
                          if (!assigneeId || !Array.isArray(projectMembers)) return '-';
                          const m = projectMembers.find((pm: any) => String(pm.user_id) === String(assigneeId));
                          return m ? (m.user_display_name || m.username || m.user_email || 'User') : '-';
                        })()}
                      </div>
                      {/* Status from workflow_status_id or legacy status */}
                      <div className="col-span-2 text-center">
                        {(() => {
                          const wsId = (s as any).workflow_status_id;
                          const legacy = (s as any).status;
                          if (wsId && Array.isArray(workflowStatuses) && workflowStatuses.length > 0) {
                            const match = workflowStatuses.find((w: any) => String(w.id) === String(wsId));
                            return match?.name || legacy || '-';
                          }
                          return legacy ? String(legacy).toUpperCase() : '-';
                        })()}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {selectedSubtasks.size > 0 && (
                <div className="mt-2 flex items-center justify-between px-3 py-2 bg-red-50 border border-red-200 rounded">
                  <span className="text-sm text-red-700">
                    {selectedSubtasks.size} subtask(s) selected
                  </span>
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={isDeletingSubtasks}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm flex items-center gap-1"
                  >
                    {isDeletingSubtasks ? 'Deleting...' : 'Delete Selected'}
                  </button>
                </div>
              )}


            {showAddSubtask && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const title = newSubtaskTitle.trim();
                  if (!title || isCreatingSubtask) return;
                  setIsCreatingSubtask(true);
                  try {
                    const payload: any = { title };
                    const typeId = newSubtaskTypeId || defaultSubtaskTypeId;
                    if (typeId) payload.task_type_id = typeId;
                    if (mediumPriorityId) {
                      payload.priority_id = mediumPriorityId;
                    } else {
                      payload.priority = 'medium';
                    }
                    const created = await subtaskAPI.create(task.id, payload);
                    setSubtasksInModal((prev) => [...prev, created]);
                    setNewSubtaskTitle('');
                    setShowAddSubtask(false);
                    setIsSubtaskTypePickerOpen(false);
                  } finally {
                    setIsCreatingSubtask(false);
                  }
                }}
                className="mt-2"
              >
                <div className="flex items-center gap-2" ref={subtaskTypePickerRef}>
                  {taskTypes.length > 0 && (
                    <div className="relative">
                      <AvatarTooltip label={selectedSubtaskType?.name || 'Task type'} position="bottom">
                        <button
                          type="button"
                          onClick={() => setIsSubtaskTypePickerOpen((prev) => !prev)}
                          className={`w-9 h-9 rounded-full border flex items-center justify-center ${isSubtaskTypePickerOpen ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                            }`}
                        >
                          <TaskTypeIcon
                            url={resolveTaskTypeIconUrl(
                              (selectedSubtaskType as any)?.icon ||
                              (selectedSubtaskType as any)?.icon_url ||
                              (selectedSubtaskType as any)?.image ||
                              (selectedSubtaskType as any)?.image_url
                            )}
                            name={selectedSubtaskType?.name}
                            className="w-4 h-4"
                          />
                        </button>
                      </AvatarTooltip>
                      {isSubtaskTypePickerOpen && (
                        <div className="absolute z-30 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-48">
                          <div className="grid grid-cols-5 gap-2">
                            {taskTypes.map((type) => {
                              const iconUrl = resolveTaskTypeIconUrl(
                                (type as any)?.icon ||
                                (type as any)?.icon_url ||
                                (type as any)?.image ||
                                (type as any)?.image_url
                              );
                              const isSelected = String(type.id) === String(newSubtaskTypeId);
                              return (
                                <AvatarTooltip key={type.id} label={type.name} position="bottom">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewSubtaskTypeId(String(type.id));
                                      setIsSubtaskTypePickerOpen(false);
                                    }}
                                    className={`w-8 h-8 rounded-full border flex items-center justify-center transition ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-gray-100'
                                      }`}
                                  >
                                    <TaskTypeIcon url={iconUrl} name={type.name} className="w-4 h-4" />
                                  </button>
                                </AvatarTooltip>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <input
                    ref={addInputRef}
                    type="text"
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    placeholder="Add subtask"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <AvatarTooltip label={"Create Task"} position="bottom">

                    <button
                      className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      type="submit"
                      disabled={isCreatingSubtask}
                      title="Create subtask"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </AvatarTooltip>
                  <AvatarTooltip label={"Cancel"} position="bottom">
                    <button
                      type="button"
                      className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                      onClick={() => {
                        setShowAddSubtask(false);
                        setNewSubtaskTitle('');
                        setIsSubtaskTypePickerOpen(false);
                      }}
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </AvatarTooltip>
                </div>
              </form>
            )}
          </div>

          {/* <SubtasksSection
            taskId={task.id}
            subtasks={subtasks}
            onAddSubtask={onAddSubtask}
            onUpdateSubtask={onUpdateSubtask}
          /> */}

          {/* <RelatedWorkSection relatedWork={relatedWork} /> */}

          <div className="mt-6">
            <h4 className="font-medium text-gray-900 mb-2">Attachments</h4>
            <FileUpload taskId={task.id} isViewerOnly={isViewerOnly} />
          </div>

          <ActivitySection
            taskId={task.id}
            activity={activity}
            comments={comments}
            onAddComment={onAddComment}
            task={task}
            onDeleteComment={onDeleteComment || (async () => { })}
            onUpdateComment={onUpdateComment}
            users={(projectMembers || []).map((m: any) => ({ id: String(m.user_id), name: m.user_display_name || m.username || m.user_email || 'User', email: m.user_email || '' }))}
            isViewerOnly={isViewerOnly}
          />

        </div>

        {/* Sidebar drag handle */}
        <div
          className="w-1 cursor-col-resize bg-transparent hover:bg-blue-200 transition-colors"
          onMouseDown={startSidebarDrag}
          aria-hidden="true"
        />

        {/* Sidebar - Right Side */}
        <div
          ref={sidebarRef}
          className="w-full max-w-md px-4 overflow-y-auto taskmodal-scroll"
          style={hasDragged ? {
            flexBasis: `${sidebarWidthPct}%`,
            maxWidth: '70%',
            minWidth: '260px',
          } : undefined}
        >
          <DetailsSidebar
            taskDetails={taskDetails}
            task={task}
            projectMembers={projectMembers}
            onUpdateAssignee={onUpdateAssignee}
            onUpdateFields={onUpdateFields}
            createTrigger={createSubtaskTrigger}
            hideSubtasks
            projectKey={projectKey}
            isViewerOnly={isViewerOnly}
          />
        </div>
      </div>
      </div>

      {/* Delete Selected Subtasks Confirmation Dialog */}
      {showDeleteSubtasksConfirm && (
        <DeleteConfirmDialog
          isOpen={showDeleteSubtasksConfirm}
          itemName={`${selectedSubtasks.size} subtask${selectedSubtasks.size > 1 ? 's' : ''}`}
          itemType="subtask"
          onConfirm={handleConfirmDeleteSelected}
          onCancel={() => !isDeletingSubtasks && setShowDeleteSubtasksConfirm(false)}
          isLoading={isDeletingSubtasks}
          customMessage={
            selectedSubtasks.size === 1
              ? 'Are you sure you want to delete this subtask? This action cannot be undone.'
              : 'Are you sure you want to delete the selected subtasks? This action cannot be undone.'
          }
        />
      )}
    </Modal>
  );
};